
/**
 * Estados posibles de un usuario según el backend
 * @constant
 */
export const USER_STATUS = {
  NOT_FOUND: 0,   // Usuario no existe
  ACTIVE: 1,      // Usuario activo
  BLOCKED: 2,     // Usuario bloqueado
};

/**
 * Estados de cambio de contraseña obligatorio
 * @constant
 */
export const RESET_PASSWORD_STATUS = {
  NORMAL: 0,      // Usuario normal
  REQUIRED: 1,    // Usuario debe cambiar contraseña
};

/**
 * Grupos de usuarios según el backend
 * @constant
 */
export const GROUP_TYPES = {
  CONCESIONARIO: 1,  // Concesionario - Ve módulos de scoring de concesionario
  HONDA: 2,          // Honda - Ve funcionalidades específicas de Honda
  XEROX: 3,          // Xerox - Acceso completo a todo el sistema
  HONDA_COMPLIANCE: 4   // Xerox - Acceso completo a todo el sistema pero viendo solo los casos de compliance (pep o laft positivos)
};

/**
 * Nombres de los grupos para mostrar en UI
 * @constant
 */
export const GROUP_NAMES = {
  [GROUP_TYPES.CONCESIONARIO]: 'Concesionario',
  [GROUP_TYPES.HONDA]: 'Honda',
  [GROUP_TYPES.XEROX]: 'Xerox',
  [GROUP_TYPES.HONDA_COMPLIANCE]: 'Honda Compliance',
};

// ============================================================================
// CONFIGURACIÓN DE API
// ============================================================================

// Obtener la URL base de la API desde las variables de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ============================================================================
// GESTIÓN DE SESIÓN
// ============================================================================

/**
 * Obtiene la sesión actual del usuario desde sessionStorage
 * @returns {Object|null} Objeto de sesión con id, username, name, email, sessionId, modules, status, userResetPassword
 */
export const getUserSession = () => {
  try {
    const session = sessionStorage.getItem('userSession');
    return session ? JSON.parse(session) : null;
  } catch (error) {
    console.error('Error al obtener la sesión del usuario:', error);
    return null;
  }
};

/**
 * Verifica si la sesión actual es válida
 * @returns {boolean} true si existe una sesión válida, false en caso contrario
 */
export const isSessionValid = () => {
  const session = getUserSession();
  return session !== null && session.id !== undefined;
};

// ============================================================================
// VALIDACIÓN DE ESTADO DE USUARIO
// ============================================================================

/**
 * Verifica si el usuario actual está activo
 * @returns {boolean} true si el usuario está activo (status === 1)
 */
export const isUserActive = () => {
  const session = getUserSession();
  if (!session) return false;
  
  // Si no hay campo status, asumimos que está activo (compatibilidad con versiones antiguas)
  if (session.status === undefined) return true;
  
  return session.status === USER_STATUS.ACTIVE;
};

/**
 * Verifica si el usuario actual está bloqueado
 * @returns {boolean} true si el usuario está bloqueado (status === 2)
 */
export const isUserBlocked = () => {
  const session = getUserSession();
  if (!session) return false;
  
  return session.status === USER_STATUS.BLOCKED;
};

/**
 * Verifica si el usuario no fue encontrado
 * @returns {boolean} true si el usuario no existe (status === 0)
 */
export const userNotFound = () => {
  const session = getUserSession();
  if (!session) return true;
  
  return session.status === USER_STATUS.NOT_FOUND;
};

/**
 * Obtiene el sessionId de la sesión actual
 * @returns {string|null} sessionId o null si no hay sesión
 */
export const getSessionId = () => {
  const session = getUserSession();
  return session?.sessionId || null;
};

// ============================================================================
// GESTIÓN DE MÓDULOS Y PERMISOS
// ============================================================================

/**
 * Normaliza un módulo a string
 * Maneja tanto strings como objetos del backend
 * @param {string|Object} module - Módulo a normalizar
 * @returns {string|null} Nombre del módulo en string o null si no se puede determinar
 */
const normalizeModule = (module) => {
  // Si ya es un string, retornarlo
  if (typeof module === 'string') {
    return module;
  }
  
  // Si es un objeto, intentar extraer el nombre del módulo
  if (typeof module === 'object' && module !== null) {
    // Intentar diferentes propiedades comunes
    return module.name || module.module || module.moduleName || 
           module.NAME || module.MODULE || module.modulE_NAME || null;
  }
  
  return null;
};

/**
 * Verifica si el usuario tiene acceso a un módulo específico
 * @param {string} moduleName - Nombre del módulo a verificar (ej: 'ADMIN', 'SCORING', 'ABM')
 * @returns {boolean} true si el usuario tiene el módulo, false en caso contrario
 */
export const hasModule = (moduleName) => {
  const session = getUserSession();
  
  // Si no hay sesión o no hay módulos, denegar acceso
  if (!session || !session.modules) {
    return false;
  }
  
  // Si modules no es un array, denegar acceso
  if (!Array.isArray(session.modules)) {
    console.warn('El campo modules en la sesión no es un array');
    return false;
  }
  
  // Normalizar el nombre del módulo a buscar
  if (!moduleName || typeof moduleName !== 'string') {
    console.warn('hasModule requiere un nombre de módulo válido (string)');
    return false;
  }
  
  const searchModuleName = moduleName.toUpperCase().trim();
  
  // Verificar si el módulo está en el array (manejando tanto strings como objetos)
  return session.modules.some(module => {
    const normalizedModule = normalizeModule(module);
    
    if (!normalizedModule) {
      console.warn('Módulo con formato no reconocido:', module);
      return false;
    }
    
    return normalizedModule.toUpperCase().trim() === searchModuleName;
  });
};

/**
 * Verifica si el usuario tiene al menos uno de los módulos especificados
 * @param {string[]} moduleNames - Array de nombres de módulos (ej: ['ADMIN', 'SUPERVISOR'])
 * @returns {boolean} true si el usuario tiene al menos uno de los módulos
 */
export const hasAnyModule = (moduleNames) => {
  if (!Array.isArray(moduleNames)) {
    console.warn('hasAnyModule espera un array de módulos');
    return false;
  }
  
  return moduleNames.some(moduleName => hasModule(moduleName));
};

/**
 * Verifica si el usuario tiene todos los módulos especificados
 * @param {string[]} moduleNames - Array de nombres de módulos (ej: ['ADMIN', 'SCORING'])
 * @returns {boolean} true si el usuario tiene todos los módulos
 */
export const hasAllModules = (moduleNames) => {
  if (!Array.isArray(moduleNames)) {
    console.warn('hasAllModules espera un array de módulos');
    return false;
  }
  
  return moduleNames.every(moduleName => hasModule(moduleName));
};

/**
 * Obtiene la lista de módulos del usuario actual (normalizados a strings)
 * @returns {string[]} Array de módulos del usuario, o array vacío si no hay sesión
 */
export const getUserModules = () => {
  const session = getUserSession();
  
  if (!session || !session.modules || !Array.isArray(session.modules)) {
    return [];
  }
  
  // Normalizar todos los módulos a strings
  return session.modules
    .map(module => normalizeModule(module))
    .filter(module => module !== null); // Filtrar módulos inválidos
};

/**
 * Cierra la sesión del usuario y limpia el sessionStorage
 */
export const clearSession = () => {
  sessionStorage.removeItem('userSession');
};

/**
 * Guarda la sesión del usuario en sessionStorage
 * @param {Object} sessionData - Datos de la sesión a guardar
 */
export const setUserSession = (sessionData) => {
  try {
    sessionStorage.setItem('userSession', JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error al guardar la sesión:', error);
  }
};

/**
 * Obtiene información básica del usuario actual
 * @returns {Object|null} Objeto con id, username, name, email, group_ID, groupname
 */
export const getCurrentUser = () => {
  const session = getUserSession();
  
  if (!session) {
    return null;
  }
  
  return {
    id: session.id,
    username: session.username,
    name: session.name,
    email: session.email,
    sessionId: session.sessionId,
    group_ID: session.group_ID,
    groupname: session.groupname
  };
};

// ============================================================================
// GESTIÓN DE GRUPOS DE USUARIOS
// ============================================================================

/**
 * Obtiene el group_ID del usuario actual
 * @returns {number|null} ID del grupo (1=Concesionario, 2=Honda, 3=Xerox) o null si no hay sesión
 */
export const getUserGroupId = () => {
  const session = getUserSession();
  return session?.group_ID ?? null;
};

/**
 * Obtiene el nombre del grupo del usuario actual
 * @returns {string|null} Nombre del grupo o null si no hay sesión
 */
export const getUserGroupName = () => {
  const session = getUserSession();
  return session?.groupname ?? null;
};

/**
 * Verifica si el usuario pertenece a un grupo específico
 * @param {number} groupId - ID del grupo a verificar (usar GROUP_TYPES)
 * @returns {boolean} true si el usuario pertenece al grupo
 */
export const isUserInGroup = (groupId) => {
  const session = getUserSession();
  
  if (!session || session.group_ID === null || session.group_ID === undefined) {
    return false;
  }
  
  return session.group_ID === groupId;
};

/**
 * Verifica si el usuario es de tipo Concesionario (group_ID === 1)
 * Los concesionarios ven módulos de scoring de concesionario
 * @returns {boolean} true si es concesionario
 */
export const isConcesionario = () => {
  return isUserInGroup(GROUP_TYPES.CONCESIONARIO);
};

/**
 * Verifica si el usuario es de tipo Honda (group_ID === 2)
 * Los usuarios Honda tienen acceso a funcionalidades específicas de Honda
 * @returns {boolean} true si es Honda
 */
export const isHonda = () => {
  return isUserInGroup(GROUP_TYPES.HONDA);
};

/**
 * Verifica si el usuario es de tipo Xerox (group_ID === 3)
 * Los usuarios Xerox tienen acceso completo a todo el sistema
 * @returns {boolean} true si es Xerox
 */
export const isXerox = () => {
  return isUserInGroup(GROUP_TYPES.XEROX);
};


//
export const isHondaCompliance = () => {
  return isUserInGroup(GROUP_TYPES.HONDA_COMPLIANCE);
};

/**
 * Verifica si el usuario tiene acceso de administrador completo
 * Actualmente, solo Xerox (group_ID === 3) tiene acceso total
 * @returns {boolean} true si tiene acceso total
 */
export const hasFullAccess = () => {
  return isXerox();
};

/**
 * Verifica si el usuario puede ver un módulo según su grupo
 * Esta función combina la verificación de módulos con la de grupos
 * 
 * @param {string} moduleName - Nombre del módulo a verificar
 * @returns {boolean} true si el usuario puede ver el módulo según su grupo y permisos
 */
export const canViewModule = (moduleName) => {
  // Xerox ve todo
  if (isXerox()) {
    if(moduleName === 'ABM') { return true; }
    return false;
   
  }

  // Concesionario siempre ve Cesiones, Prendas y Scoring
  if (isConcesionario()) {
    const concesionarioModules = ['CESIONES', 'PRENDAS', 'SCORING'];
    if (concesionarioModules.includes(moduleName?.toUpperCase?.() ?? '')) {
      return true;
    }
  }
  
  // Para otros grupos, verificar si tienen el módulo asignado
  return hasModule(moduleName);
};

/**
 * Obtiene el nombre legible del grupo del usuario
 * @returns {string} Nombre del grupo o 'Sin grupo' si no está asignado
 */
export const getGroupDisplayName = () => {
  const groupId = getUserGroupId();
  
  if (groupId === null || groupId === undefined) {
    return 'Sin grupo';
  }
  
  return GROUP_NAMES[groupId] || `Grupo ${groupId}`;
};

// ============================================================================
// PERMISOS ESPECÍFICOS DE SUBMÓDULOS DE SCORING POR GRUPO
// ============================================================================

/**
 * Verifica si el usuario puede acceder a "Análisis Crediticio Disponible"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 * @returns {boolean} true si puede acceder
 */
export const canAccessAnalisisCrediticio = () => {
  if (isXerox()) return true; // Xerox ve todo
  if (isConcesionario()) return true; // Concesionarios pueden hacer análisis
  return false; // Honda no puede
};

/**
 * Verifica si el usuario puede acceder a "Análisis de Casos"
 * CONCESIONARIO: ❌ No puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 * @returns {boolean} true si puede acceder
 */
export const canAccessDetalleCaso = () => {
  if (isXerox()) return true; // Xerox ve todo
  if (isHonda()) return true; // Honda puede ver detalles
  if (isHondaCompliance()) return true; // Honda Compliance puede ver detalles
  return false; // Concesionario no puede
};

/**
 * Verifica si el usuario puede acceder a "Control de Casos"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 * @returns {boolean} true si puede acceder
 */
export const canAccessControlCasos = () => {
  if (isXerox()) return true; // Xerox ve todo
  if (isConcesionario()) return true; // Concesionarios pueden controlar sus casos
  return false; // Honda no puede
};

/**
 * Verifica si el usuario puede acceder a "Búsqueda de Casos"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 * @returns {boolean} true si puede acceder (todos pueden)
 */
export const canAccessBusquedaCasos = () => {
  if (isXerox()) return true; // Xerox ve todo
  if (isConcesionario()) return true; // Concesionarios pueden buscar
  if (isHonda()) return true; // Honda puede buscar
  if (isHondaCompliance()) return true; // Honda Compliance puede buscar
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Tracking de Eventos"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 * @returns {boolean} true si puede acceder (todos pueden)
 */
export const canAccessTrackingEventos = () => {
  if (isXerox()) return true; // Xerox ve todo
  if (isConcesionario()) return true; // Concesionarios pueden ver tracking
  if (isHonda()) return true; // Honda puede ver tracking
  if (isHondaCompliance()) return true; // Honda Compliance puede ver tracking
  return false;
};

// ============================================================================
// PERMISOS ESPECÍFICOS DE SUBMÓDULOS DE CESIONES POR GRUPO
// ============================================================================

/**
 * Verifica si el usuario puede acceder a "Iniciar Cesión"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesIniciar = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Control de Cesiones"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesControl = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Aprobación de Cesión"
 * CONCESIONARIO: ❌ No puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesAprobacion = () => {
  if (isXerox()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Búsqueda de Cesiones"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesBusqueda = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Tracking de Cesiones"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesTracking = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Revision de Cesiones" (listado EN REVISION)
 * CONCESIONARIO: ❌ No puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessCesionesRevision = () => {
  if (isXerox()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Visualizacion de Cesion"
 * Todos los usuarios con acceso al módulo CESIONES pueden ver el detalle
 */
export const canAccessCesionesVisualizacion = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

// ============================================================================
// PERMISOS ESPECÍFICOS DE SUBMÓDULOS DE PRENDAS POR GRUPO
// ============================================================================

/**
 * Verifica si el usuario puede acceder a "Prendas Disponibles"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessPrendasDisponibles = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Control de Prendas"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ❌ No puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessPrendasControl = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Evaluación de Prendas"
 * CONCESIONARIO: ❌ No puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessPrendasEvaluacion = () => {
  if (isXerox()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Búsqueda de Prendas"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessPrendasBusqueda = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Tracking de Prendas"
 * CONCESIONARIO: ✅ Puede ver
 * HONDA: ✅ Puede ver
 * XEROX: ✅ Puede ver (acceso completo)
 */
export const canAccessPrendasTracking = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

/**
 * Verifica si el usuario puede acceder a "Visualización de Prenda"
 * Todos los grupos con acceso a PRENDAS
 */
export const canAccessPrendasVisualizacion = () => {
  if (isXerox()) return true;
  if (isConcesionario()) return true;
  if (isHonda()) return true;
  return false;
};

// ============================================================================
// WRAPPER DE API CON MANEJO AUTOMÁTICO DE SESIÓN
// ============================================================================

/**
 * Razones por las que una sesión puede ser inválida
 */
export const SESSION_INVALID_REASON = {
  EXPIRED: 'expired',                    // Sesión expirada por tiempo
  CONCURRENT_LOGIN: 'concurrent_login',  // Login desde otro dispositivo
  LOGOUT: 'logout',                       // Logout manual
  UNAUTHORIZED: 'unauthorized',           // No autorizado (401 genérico)
  USER_INACTIVE: 'user_inactive',         // Usuario puesto como inactivo
  USER_BLOCKED: 'user_blocked',           // Usuario bloqueado
};

/**
 * Callback que se ejecuta cuando se detecta una sesión inválida (401)
 * Por defecto cierra sesión y redirige al login
 * Puede ser sobrescrito para personalizar el comportamiento
 * 
 * @param {string} reason - Razón de la invalidación (SESSION_INVALID_REASON)
 */
export let onSessionInvalid = (reason = SESSION_INVALID_REASON.UNAUTHORIZED) => {
  clearSession();
  
  // Puedes mostrar diferentes mensajes según la razón
  if (reason === SESSION_INVALID_REASON.CONCURRENT_LOGIN) {
    console.warn('Sesión cerrada: login desde otro dispositivo');
  } else if (reason === SESSION_INVALID_REASON.EXPIRED) {
    console.warn('Sesión cerrada: sesión expirada');
  }
  
  window.location.href = '/login';
};

/**
 * Permite configurar el callback de sesión inválida
 * @param {Function} callback - Función a ejecutar cuando la sesión es inválida
 */
export const setSessionInvalidCallback = (callback) => {
  if (typeof callback === 'function') {
    onSessionInvalid = callback;
  }
};

/**
 * Wrapper para fetch que agrega automáticamente sessionId en headers
 * y maneja errores de sesión (401)
 * 
 * @param {string} url - URL a la que hacer la petición
 * @param {Object} options - Opciones del fetch (method, body, headers, etc.)
 * @returns {Promise<Response>} Promesa con la respuesta del fetch
 */
export const authenticatedFetch = async (url, options = {}) => {
  const sessionId = getSessionId();
  
  // Preparar headers
  const headers = {
    'Content-Type': 'application/json',
    Accept: '*/*',
    ...options.headers,
  };
  
  // Agregar sessionId si existe
  if (sessionId) {
    headers['X-Session-Id'] = sessionId;
    // También soportar otros formatos comunes de header
    headers['Session-Id'] = sessionId;
  }
  
  // Hacer el fetch
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Si es 401, la sesión es inválida
  if (response.status === 401) {
    // Intentar determinar la razón del 401
    let reason = SESSION_INVALID_REASON.UNAUTHORIZED;
    
    try {
      const errorData = await response.clone().json();
      
      // El backend puede enviar información sobre por qué es 401
      if (errorData.reason === 'concurrent_login' || 
          errorData.message?.toLowerCase().includes('otro dispositivo') ||
          errorData.message?.toLowerCase().includes('sesión activa en otro lugar')) {
        reason = SESSION_INVALID_REASON.CONCURRENT_LOGIN;
      } else if (errorData.reason === 'expired' || 
                 errorData.message?.toLowerCase().includes('expirada')) {
        reason = SESSION_INVALID_REASON.EXPIRED;
      }
    } catch (e) {
      // Si no se puede parsear la respuesta, usar razón genérica
    }
    
    onSessionInvalid(reason);
    throw new Error('Sesión inválida o expirada');
  }
  
  return response;
};

/**
 * Wrapper simplificado para hacer peticiones GET con autenticación
 * @param {string} url - URL a consultar
 * @returns {Promise<any>} Promesa con los datos parseados
 */
export const apiGet = async (url) => {
  const response = await authenticatedFetch(url, { method: 'GET' });
  
  if (!response.ok) {
    throw new Error(`Error en GET ${url}: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Wrapper simplificado para hacer peticiones POST con autenticación
 * @param {string} url - URL a la que enviar datos
 * @param {Object} data - Datos a enviar en el body
 * @returns {Promise<any>} Promesa con los datos de respuesta parseados
 */
export const apiPost = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`Error en POST ${url}: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Wrapper simplificado para hacer peticiones PUT con autenticación
 * @param {string} url - URL a la que actualizar datos
 * @param {Object} data - Datos a enviar en el body
 * @returns {Promise<any>} Promesa con los datos de respuesta parseados
 */
export const apiPut = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`Error en PUT ${url}: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Wrapper simplificado para hacer peticiones DELETE con autenticación
 * @param {string} url - URL del recurso a eliminar
 * @returns {Promise<any>} Promesa con los datos de respuesta parseados
 */
export const apiDelete = async (url) => {
  const response = await authenticatedFetch(url, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`Error en DELETE ${url}: ${response.statusText}`);
  }
  
  return response.json();
};

// ============================================================================
// VALIDACIÓN DE SESIÓN CONCURRENTE
// ============================================================================

/**
 * Variable para almacenar el intervalo de polling de sesión
 */
let sessionPollingInterval = null;

/**
 * Verifica si el usuario actual está activo en el backend
 * Consulta el estado actual del usuario para detectar si fue puesto como inactivo o bloqueado
 * 
 * @returns {Promise<{isValid: boolean, reason: string|null}>} Objeto con isValid y reason si aplica
 */
export const checkUserStatus = async () => {
  const session = getUserSession();
  
  if (!session || !session.username) {
    return { isValid: true, reason: null }; // No invalidar si no hay datos
  }
  
  try {
    // Consultar información del usuario desde el backend
    const response = await fetch(`${API_BASE_URL}/api/User/GetUserInfo?userName=${encodeURIComponent(session.username)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (response.status === 404) {
      console.warn('⚠️ Endpoint de información de usuario no disponible (404) - Manteniendo sesión');
      return { isValid: true, reason: null };
    }
    
    if (!response.ok) {
      console.warn('⚠️ Error al verificar estado de usuario:', response.status, '- Manteniendo sesión');
      return { isValid: true, reason: null };
    }
    
    const userInfo = await response.json();
    
    // userStatus: 0 = INACTIVO, 1 = ACTIVO, 2 = BLOQUEADO
    if (userInfo.userStatus === 0) {
      console.warn('❌ Usuario puesto como INACTIVO');
      return { isValid: false, reason: SESSION_INVALID_REASON.USER_INACTIVE };
    }
    
    if (userInfo.userStatus === 2) {
      console.warn('❌ Usuario BLOQUEADO');
      return { isValid: false, reason: SESSION_INVALID_REASON.USER_BLOCKED };
    }
    
    return { isValid: true, reason: null };
  } catch (error) {
    console.error('⚠️ Error al verificar estado de usuario:', error, '- Manteniendo sesión');
    return { isValid: true, reason: null }; // No cerrar sesión por errores de red
  }
};

/**
 * Verifica si la sesión actual es válida en el backend
 * Compara el sessionId local con el que el backend tiene para este usuario
 * Y verifica que el usuario siga activo (no inactivo ni bloqueado)
 * 
 * IMPORTANTE: Solo invalida sesión si hay CONFIRMACIÓN de que existe otra sesión
 * o que el usuario fue puesto como inactivo/bloqueado.
 * NO invalida por errores de red, timeouts, o errores del servidor.
 * 
 * @returns {Promise<{isValid: boolean, reason: string|null}>} Objeto con isValid y reason si aplica

 */
export const checkSessionValidity = async () => {
  const session = getUserSession();
  
  if (!session || !session.username || !session.sessionId) {
    return { isValid: true, reason: null }; // No invalidar si no hay datos para verificar
  }
  
  try {
    // 1. Verificar estado del usuario (activo/inactivo/bloqueado)
    const userStatusCheck = await checkUserStatus();
    
    if (!userStatusCheck.isValid) {
      return userStatusCheck; // Retornar inmediatamente si usuario no está activo
    }
    
    // 2. Verificar sessionId (login concurrente)
    // Usar el endpoint GetUserInfo que retorna toda la información del usuario incluyendo sessionID
    const response = await fetch(`${API_BASE_URL}/api/User/GetUserInfo?userName=${encodeURIComponent(session.username)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    // Si el endpoint no existe (404) o hay error de servidor, asumir sesión válida
    // Solo invalidar si hay una respuesta exitosa con sessionId diferente
    if (response.status === 404) {
      console.warn('⚠️ Endpoint de validación no disponible (404) - Manteniendo sesión');
      return { isValid: true, reason: null }; // Mantener sesión si el endpoint no existe
    }
    
    if (!response.ok) {
      console.warn('⚠️ Error al verificar sesión:', response.status, '- Manteniendo sesión');
      return { isValid: true, reason: null }; // Mantener sesión en caso de error
    }
    
    // El backend retorna un objeto JSON con toda la información del usuario
    const userInfo = await response.json();
    
    // Extraer el sessionID del objeto de respuesta
    const backendSessionId = userInfo.sessionID;
    
    // Si el sessionId del backend es diferente al local, la sesión fue invalidada
    if (backendSessionId !== session.sessionId) {
      console.warn('❌ SessionId diferente detectado - Sesión invalidada por login en otro dispositivo');
      return { isValid: false, reason: SESSION_INVALID_REASON.CONCURRENT_LOGIN };
    }
    
    return { isValid: true, reason: null };
  } catch (error) {
    console.error('⚠️ Error al validar sesión:', error, '- Manteniendo sesión');
    return { isValid: true, reason: null }; // No cerrar sesión por errores de red
  }
};

/**
 * Inicia un polling periódico para validar la sesión
 * Si detecta que la sesión fue invalidada (login en otro dispositivo),
 * ejecuta el callback onSessionInvalid automáticamente
 * 
 * Usa el endpoint /api/User/GetUserInfo del backend para obtener el sessionID actual
 * 
 * @param {number} intervalMs - Intervalo en milisegundos para verificar (default: 60000 = 1 minuto)
 */
export const startSessionPolling = (intervalMs = 60000) => {
  // Detener polling anterior si existe
  stopSessionPolling();
  
  sessionPollingInterval = setInterval(async () => {
    // Solo verificar si hay sesión activa
    if (!isSessionValid()) {
      stopSessionPolling();
      return;
    }
    
    const result = await checkSessionValidity();
    
    if (!result.isValid) {
      console.warn('Sesión invalidada detectada por polling - Razón:', result.reason);
      stopSessionPolling();
      onSessionInvalid(result.reason);
    }
  }, intervalMs);
};

/**
 * Detiene el polling de validación de sesión
 */
export const stopSessionPolling = () => {
  if (sessionPollingInterval) {
    clearInterval(sessionPollingInterval);
    sessionPollingInterval = null;
  }
};

// ============================================================================
// UTILIDADES DE AUTENTICACIÓN
// ============================================================================

/**
 * Valida la respuesta del login y retorna información estructurada
 * sobre el estado del usuario
 * 
 * @param {Object} loginResponse - Respuesta del backend del endpoint de login
 * @returns {Object} Objeto con información de validación
 */
export const validateLoginResponse = (loginResponse) => {
  if (!loginResponse) {
    return {
      canLogin: false,
      notFound: true,
      isBlocked: false,
      isInactive: false,
      mustResetPassword: false,
      isActive: false,
      message: 'Respuesta de login inválida',
    };
  }
  
  // Campos de la API en camelCase
  const status = loginResponse.userStatus ?? USER_STATUS.ACTIVE; // Default a ACTIVE si no viene
  const resetPassword = loginResponse.passwordResetFlag ?? RESET_PASSWORD_STATUS.NORMAL;
  const hasValidId = loginResponse.id && loginResponse.id > 0;
  
  // Usuario inactivo: status 0 pero con ID válido (cuenta desactivada por admin)
  const isInactive = status === USER_STATUS.NOT_FOUND && hasValidId;
  
  // Usuario no encontrado: status 0 y sin ID válido (credenciales incorrectas)
  const notFound = status === USER_STATUS.NOT_FOUND && !hasValidId;
  
  const isBlocked = status === USER_STATUS.BLOCKED;
  const isActive = status === USER_STATUS.ACTIVE;
  const mustResetPassword = resetPassword === RESET_PASSWORD_STATUS.REQUIRED;
  
  // Puede hacer login si está activo y no requiere cambio de contraseña
  const canLogin = isActive && !mustResetPassword && !notFound && !isBlocked && !isInactive;
  
  // Puede proceder (aunque requiera cambio de contraseña) si está activo
  const canProceed = isActive && !notFound && !isBlocked && !isInactive;
  
  let message = '';
  if (notFound) {
    message = 'Usuario no encontrado o credenciales incorrectas';
  } else if (isInactive) {
    message = 'Cuenta desactivada. Contacte al administrador';
  } else if (isBlocked) {
    message = 'Usuario bloqueado. Contacte al administrador';
  } else if (mustResetPassword) {
    message = 'Debe cambiar su contraseña';
  } else if (canLogin) {
    message = 'Login exitoso';
  }
  
  return {
    canLogin,
    canProceed,
    notFound,
    isBlocked,
    isInactive,
    isActive,
    mustResetPassword,
    message,
    status,
    resetPasswordStatus: resetPassword,
  };
};
